import { type DocBlock } from './_Doc.ts'
import { HEADING_2, NORMAL_TEXT, TokenStream } from './_parsing.ts'

const TherapyID__Brand: unique symbol = Symbol()
export type TherapyID = string & { [TherapyID__Brand]: never}

export const GAHTFHT_ID = 'imago.gaht.fht' as TherapyID
export const GAHTMHT_ID = 'imago.gaht.mht' as TherapyID

export interface RecommendationContent {
	therapy: TherapyID | undefined
	advice: string
	items: {
		title: string
		quantity: string
		dosageRegimen: string
	}[]
	reviewIn: string
	reviewRequiredTests: string[]
	reviewNotes: string
}

export const STANDARD_TESTS = [
	'full blood count',
	'liver function tests',
	'lipids',
	'testosterone and estradiol',
	'prolactin',
	'LH and FSH',
	'urea and electrolytes',
	'fasting glucose',
	'blood presure reading',
	'kidney function tests (creatinine and cystatin C)',
]

const therapyNames = new Map<TherapyID, string>([
	[GAHTFHT_ID, 'FHT'],
	[GAHTMHT_ID, 'MHT'],
])

const therapiesByName = new Map([...therapyNames].map(([id, name]) => [name, id]))

export function docifyRecommendationContent(pc: RecommendationContent): DocBlock[] {
	return [
		...(pc.therapy ? [{class: NORMAL_TEXT, text: `Therapy: ${therapyNames.get(pc.therapy)}`}] : []),
		...(pc.advice ? [
			{class: HEADING_2, text: "Advice"},
			{class: NORMAL_TEXT, text: pc.advice},
		] : []),
		...pc.items.flatMap(item => [
			{class: HEADING_2, text: item.title},
			{class: NORMAL_TEXT, text: item.dosageRegimen},
			{class: NORMAL_TEXT, text: `Supply ${item.quantity}`},
		] satisfies DocBlock[]),
		...(pc.reviewIn ? [
			{class: HEADING_2, text: "Review"},
			{class: NORMAL_TEXT, text: `Review in: ${pc.reviewIn}`},
			{class: NORMAL_TEXT, text: `Required tests: ${pc.reviewRequiredTests.join(', ')}`},
			{class: NORMAL_TEXT, text: `${pc.reviewNotes}`},
		] : []),
	]
}

function shiftMatching(lines: string[], prefix: string) {
	for (let i = 0; i < lines.length; ++i) {
		const line = lines[i]!
		if (line.startsWith(prefix)) {
			lines.splice(i, 1)
			return line.substring(prefix.length)
		}
	}
}

export function parseRecommendationContent(ts: TokenStream): RecommendationContent {
	let advice: string = ""
	let reviewIn: string = ""
	let reviewRequiredTests: string = ""
	let reviewNotes: string = ""
	let items: {
		title: string
		quantity: string
		dosageRegimen: string
	}[] = []

	const content = ts.consumePlainContent().split('\n')
	const therapyName = shiftMatching(content, 'Therapy: ') ?? ''
	const therapy = therapiesByName.get(therapyName)

	let heading
	while (heading = ts.tryMatchClass(HEADING_2)) {
		const content = ts.consumePlainContent().split('\n')
		if (heading === "Advice") {
			advice = content.join('\n')
		} else if (heading === "Review") {
			if (!content[0]!.startsWith('Review in: ')) {
				// Legacy syntax
				reviewIn = content[0]!
				continue
			}

			reviewIn = shiftMatching(content, 'Review in: ') ?? ''
			reviewRequiredTests = shiftMatching(content, 'Required tests: ') ?? ''
			reviewNotes = content.join('\n')
		} else {
			items.push({
				title: heading,
				quantity: shiftMatching(content, 'Supply ') ?? '',
				dosageRegimen: content.join('\n'),
			})
		}
	}

	return {
		therapy,
		advice,
		items,
		reviewIn,
		reviewRequiredTests: reviewRequiredTests.split(', '),
		reviewNotes,
	}
}
